import { HStack, Progress, Text, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import CustomButton from '@/components/atoms/CustomButton';
import ModalContainer from '@/components/molecules/ModalContainer';
import { extra, text, text as textColors } from '@/theme/colors/palettes';
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions';
import queryKeys from '@/constants/queryKeys';
import { contentCreatorRoutes } from '@/constants/routes/app';
import useContentImageUpload from '@/hooks/content/useContentImageUpload';
import useCreateContent from '@/hooks/content/useCreateContent';
import useToast from '@/hooks/useToast';
import { buildContentDetails, collectImageUploads } from '@/modules/content/creation/model/contentPayload';
import { TContentFormMode, TContentFormSubmit } from '@/modules/content/creation/model/types';
import ContentWizard from '@/modules/content/creation/ContentWizard';
import svgIcons from '@/assets/svg/icons';
import { TCustomAxiosError } from '@/services/api';
import { createMuxUploadUrl, publishContent, setYoutubeVideoSource } from '@/services/api/requests/content';
import { uploadFileToMux } from '@/services/muxUpload';
import { useAppStore } from '@/store/useAppStore';
import { TContentType } from '@/types/Content.types';
import roles from '@/constants/roles';

const SUPPORTED_TYPES: TContentType[] = ['post', 'article', 'video'];

type ContentCreateSearch = { type: TContentType };

const CreatorContentCreate = () => {
  const { type } = Route.useSearch();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user, organization } = useAppStore();

  const isSupported = SUPPORTED_TYPES.includes(type);

  const { mutateAsync: createContent } = useCreateContent();
  const { uploadThumbnailAsync, uploadArticleImageAsync } = useContentImageUpload();
  const [isSaving, setIsSaving] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);

  // Branding shown on the thumbnail / preview card — the creator's name + organization.
  const authorLabel = useMemo(() => {
    const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.name || 'You';
    return organization?.name ? `${name} | ${organization.name}` : name;
  }, [user, organization]);

  const orgId = user?.app_metadata?.primaryOrganizationId;
  const userRoles = user?.roles ?? [];
  const allowOrganizationVisibility = !(userRoles.includes(roles.contentCreator) && userRoles.length === 1);

  const goToList = () => navigate({ to: contentCreatorRoutes.creatorProfile });

  const handleSubmit = async (mode: TContentFormMode, { values, thumbnailFile }: TContentFormSubmit) => {
    setIsSaving(true);
    try {
      const created = await createContent({
        type,
        title: values.title,
        description: values.description,
        domains: values.domains,
        subdomains: values.subdomains,
        visibility:
          !allowOrganizationVisibility || values.isPublic
            ? { public: true, orgIds: [] }
            : { public: false, orgIds: orgId ? [orgId] : [] },
        details: buildContentDetails(type, values),
      });

      const id = created.data.id;

      if (thumbnailFile) {
        await uploadThumbnailAsync({ id, file: thumbnailFile });
      }

      if (type === 'video') {
        // Set the chosen source. YouTube can publish immediately; a Mux upload must
        // finish uploading, but publishing will happen in the background
        let publishedYoutube = false;
        let queuedMuxPublish = false;
        if (values.videoSource === 'youtube' && values.youtubeUrl.trim()) {
          await setYoutubeVideoSource(id, values.youtubeUrl.trim());
          if (mode === 'publish') {
            await publishContent(id);
            publishedYoutube = true;
          }
        } else if (values.videoSource === 'mux' && values.videoFile) {
          const { uploadUrl } = await createMuxUploadUrl(id);
          setUploadPercent(0);
          await uploadFileToMux(uploadUrl, values.videoFile, setUploadPercent);
          setUploadPercent(null);

          if (mode === 'publish') {
            await publishContent(id);
            queuedMuxPublish = true;
          }
        }

        await queryClient.invalidateQueries({ queryKey: [queryKeys.content.mine] });
        toast({
          status: 'success',
          title: 'Success',
          description: publishedYoutube
            ? 'Reel published.'
            : queuedMuxPublish
              ? 'Reel uploaded. It will publish automatically when processing finishes.'
            : values.videoSource === 'mux'
              ? 'Reel uploaded. It can be published once processing finishes.'
              : 'Draft saved.',
        });
        goToList();
        return;
      }

      for (const { sectionId, subsectionId, file } of collectImageUploads(values)) {
        await uploadArticleImageAsync({ id, file, sectionId, subsectionId });
      }

      if (mode === 'publish') {
        await publishContent(id);
      }

      await queryClient.invalidateQueries({ queryKey: [queryKeys.content.mine] });
      toast({
        status: 'success',
        title: 'Success',
        description: mode === 'publish' ? 'Content published.' : 'Draft saved.',
      });
      goToList();
    } catch (error) {
      setUploadPercent(null);
      toast({
        status: 'error',
        title: 'Error saving content',
        description: (error as TCustomAxiosError)?.errors?.[0]?.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <VStack width={MAX_CONTAINER_WIDTH} gap={'8px'} alignItems={'stretch'}>
      {/* Blocking overlay with a progress bar while the reel uploads to Mux */}
      <ModalContainer
        isOpen={uploadPercent !== null}
        onClose={() => {}}
        width={'720px'}
        backgroundColor={extra.white}
        closeOnOverlayClick={false}
        closeButtonHidden
      >
        <VStack paddingX={'32px'} paddingY={'36px'} gap={'18px'} align={'stretch'}>
          <Text variant={'loraMediumTitle'} color={textColors.darkBlue} textAlign={'center'}>
            Uploading your reel
          </Text>
          <Text fontSize={'14px'} color={textColors.mediumBlue} textAlign={'center'}>
            Please keep this tab open until the upload finishes.
          </Text>
          <Progress
            value={uploadPercent ?? 0}
            size={'md'}
            borderRadius={'full'}
            colorScheme={'purple'}
            hasStripe
            isAnimated
          />
          <Text fontSize={'13px'} fontWeight={700} color={textColors.mediumBlue} textAlign={'center'}>
            {uploadPercent ?? 0}%
          </Text>
        </VStack>
      </ModalContainer>

      {!isSupported ? (
        <>
          <HStack>
            <CustomButton
              isTransparent
              iconBeforeLabelSvgPath={svgIcons.leftArrow}
              iconBeforeLabelSvgBoxHeight={12}
              iconBeforeLabelSvgColor={text.mediumBlue}
              labelColor={'text.mediumBlue'}
              labelHoverColor={'extra.black'}
              label={'Back to Your Content'}
              onClick={goToList}
            />
          </HStack>
          <Text color={'text.mediumBlue'} marginTop={'20px'}>
            This content type isn’t available yet.
          </Text>
        </>
      ) : (
        <ContentWizard
          type={type}
          authorLabel={authorLabel}
          defaultValues={{ isPublic: !allowOrganizationVisibility }}
          allowOrganizationVisibility={allowOrganizationVisibility}
          isSubmitting={isSaving}
          onExit={goToList}
          onSubmit={handleSubmit}
          secondaryAction={{ key: 'draft', label: 'Save as Draft', mode: 'draft', isTransparent: true }}
          primaryAction={{
            key: 'publish',
            label: 'Publish Content',
            mode: 'publish',
            backgroundColor: 'secondary.500',
          }}
        />
      )}
    </VStack>
  );
};

export const Route = createFileRoute('/_authenticated/_creator/_content/creator/content/create')({
  validateSearch: (search: Record<string, unknown>): ContentCreateSearch => ({
    type: search.type === 'article' || search.type === 'video' ? (search.type as TContentType) : 'post',
  }),
  staticData: { title: 'Create Content' },
  component: CreatorContentCreate,
});
